import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Check, GitPullRequest, X } from 'lucide-react';
import { ApiError, type ApprovalVerdict, type CardApprovals } from '@molt/shared';

import { Avatar, Button, TextField, profileName } from '@/components';
import { announce } from '@/lib/announce';
import {
  useResetCardApprovals,
  useSetCardApproval,
  useWithdrawCardApproval,
} from '@/lib/card-approvals';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { formatLocaleDateTimeLabel } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';

export interface CardApprovalSectionProps {
  readonly cardId: string;
  readonly boardId: string;
  readonly approvals: CardApprovals;
  /** The signed-in member, to find their own verdict among the entries. */
  readonly currentUserId?: string | undefined;
  /**
   * The viewer may write this card (a board member, card not archived, board
   * open). The section never decides this; `CardBack` does.
   */
  readonly canWrite: boolean;
}

/**
 * PR-list approvals on the card back. A verdict's reason is NOT a comment: it
 * lives on the verdict, is saved with it, and is shown here and in the MCP card
 * detail, never in the activity stream.
 *
 * Shown when the card is in the PR list or already carries verdicts; with
 * neither there is nothing to say. The controls need both a writer and a card
 * still in the PR list. Out of the list the section is read-only history,
 * except that the caller may still withdraw their own verdict.
 *
 * "Reset approvals" removes EVERY member's verdict, not only the caller's, for
 * a card that needs more work and must be revalidated. Any writer may use it,
 * in or out of the PR list, whenever there is at least one verdict to remove.
 * It confirms inline in two steps rather than through a dialog: the first
 * click only reveals the question and its Confirm / Cancel pair.
 */
export function CardApprovalSection({
  cardId,
  boardId,
  approvals,
  currentUserId,
  canWrite,
}: CardApprovalSectionProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const localePreferences = useLocalePreferencesValue();
  const headingId = useId();
  const reasonId = useId();
  const resetPromptId = useId();
  const setApproval = useSetCardApproval();
  const withdraw = useWithdrawCardApproval();
  // `resetAll`, not `reset`: a mutation result already has a `.reset()` method.
  const resetAll = useResetCardApprovals();
  const [confirmingReset, setConfirmingReset] = useState(false);
  const resetRevealRef = useRef<HTMLButtonElement>(null);
  const resetCancelRef = useRef<HTMLButtonElement>(null);
  // The question replaces the reveal button, so focus follows it there and
  // back rather than dropping to `body` when the focused control unmounts.
  const resetFocusTarget = useRef<'cancel' | 'reveal' | null>(null);
  useEffect(() => {
    const target = resetFocusTarget.current;
    resetFocusTarget.current = null;
    if (target === 'cancel') resetCancelRef.current?.focus();
    else if (target === 'reveal') resetRevealRef.current?.focus();
  }, [confirmingReset]);

  const mine =
    currentUserId === undefined
      ? undefined
      : approvals.entries.find((entry) => entry.user.id === currentUserId);
  const mineReason = mine?.reason ?? '';
  const [reason, setReason] = useState(mineReason);
  // A verdict changed elsewhere (another tab, over the socket) replaces the draft.
  useEffect(() => setReason(mineReason), [mineReason, mine?.updatedAt]);

  const canReset = canWrite && approvals.entries.length > 0;
  // An open question must not outlive its subject: when the verdicts empty out
  // elsewhere (another member's reset over the socket) or writing is lost, the
  // question closes, so a later verdict does not bring it back already open.
  useEffect(() => {
    if (!canReset) setConfirmingReset(false);
  }, [canReset]);

  if (!approvals.inPrList && approvals.entries.length === 0) return null;

  const canVerdict = canWrite && approvals.inPrList;
  const pending = setApproval.isPending || withdraw.isPending || resetAll.isPending;
  const error = setApproval.error ?? withdraw.error ?? resetAll.error;

  // A mutation keeps its error until it runs again or is reset, so starting one
  // clears the other two: only the latest gesture's failure is ever shown.
  function submit(verdict: ApprovalVerdict): void {
    withdraw.reset();
    resetAll.reset();
    setApproval.mutate(
      { boardId, cardId, verdict, reason: reason.trim() === '' ? null : reason },
      {
        onSuccess: () => {
          announce(
            verdict === 'approved'
              ? t('cardBack.approvals.announce.approved')
              : t('cardBack.approvals.announce.rejected'),
          );
        },
      },
    );
  }

  function withdrawMine(): void {
    setApproval.reset();
    resetAll.reset();
    withdraw.mutate(
      { boardId, cardId },
      {
        onSuccess: () => {
          announce(t('cardBack.approvals.announce.withdrawn'));
        },
      },
    );
  }

  function revealResetConfirm(): void {
    resetFocusTarget.current = 'cancel';
    setConfirmingReset(true);
  }

  function cancelReset(): void {
    resetFocusTarget.current = 'reveal';
    setConfirmingReset(false);
  }

  function resetEveryVerdict(): void {
    setApproval.reset();
    withdraw.reset();
    resetAll.mutate(
      { boardId, cardId },
      {
        onSuccess: () => {
          setConfirmingReset(false);
          announce(t('cardBack.approvals.announce.reset'));
        },
      },
    );
  }

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-2 rounded-card border border-border bg-surface-raised px-3 py-2.5"
    >
      <div className="flex items-center gap-2">
        <GitPullRequest aria-hidden="true" className="size-4 text-ink-muted" />
        <h2 id={headingId} className="text-sm font-semibold text-ink">
          {t('cardBack.approvals.heading')}
        </h2>
        {approvals.met ? (
          <span className="text-xs font-medium text-ok">
            {t('cardBack.approvals.met')}
          </span>
        ) : null}
      </div>

      <p className="text-sm text-ink-muted">
        {t('cardBack.approvals.summary', {
          approved: approvals.approvedCount,
          required: approvals.requiredApprovals,
          rejected: approvals.rejectedCount,
        })}
      </p>

      {approvals.inPrList ? null : (
        <p className="text-sm text-ink-muted">{t('cardBack.approvals.notInPrList')}</p>
      )}

      {canVerdict ? (
        <div className="flex flex-col gap-2">
          <TextField
            id={reasonId}
            multiline
            label={t('cardBack.approvals.reasonLabel')}
            value={reason}
            maxLength={2000}
            onChange={(event) => setReason(event.target.value)}
          />
          <p className="text-xs text-ink-muted">{t('cardBack.approvals.reasonHint')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="toggle"
              pressed={mine?.verdict === 'approved'}
              busy={pending}
              iconStart={<Check aria-hidden="true" className="size-4" />}
              onClick={() => submit('approved')}
            >
              {t('cardBack.approvals.approve')}
            </Button>
            <Button
              size="sm"
              variant="toggle"
              pressed={mine?.verdict === 'rejected'}
              busy={pending}
              iconStart={<X aria-hidden="true" className="size-4" />}
              onClick={() => submit('rejected')}
            >
              {t('cardBack.approvals.reject')}
            </Button>
            {mine === undefined ? null : (
              <Button size="sm" variant="quiet" busy={pending} onClick={withdrawMine}>
                {t('cardBack.approvals.withdraw')}
              </Button>
            )}
          </div>
        </div>
      ) : canWrite && mine !== undefined ? (
        <div>
          <Button size="sm" variant="quiet" busy={pending} onClick={withdrawMine}>
            {t('cardBack.approvals.withdraw')}
          </Button>
        </div>
      ) : null}

      {!canReset ? null : confirmingReset ? (
        <div
          role="group"
          aria-labelledby={resetPromptId}
          className="flex flex-col gap-2 border-t border-border pt-2"
        >
          <p id={resetPromptId} className="text-sm text-ink">
            {t(
              'cardBack.approvals.resetPrompt',
              { count: approvals.entries.length },
              approvals.entries.length,
            )}
          </p>
          <p className="text-xs text-ink-muted">{t('cardBack.approvals.resetHint')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="danger" busy={pending} onClick={resetEveryVerdict}>
              {t('cardBack.approvals.resetConfirm', undefined, approvals.entries.length)}
            </Button>
            <Button
              ref={resetCancelRef}
              size="sm"
              variant="quiet"
              disabled={resetAll.isPending}
              onClick={cancelReset}
            >
              {t('cardBack.approvals.resetCancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-t border-border pt-2">
          <Button
            ref={resetRevealRef}
            size="sm"
            variant="quiet"
            busy={pending}
            onClick={revealResetConfirm}
          >
            {t('cardBack.approvals.reset')}
          </Button>
        </div>
      )}

      {error === null ? null : (
        <p role="status" className="text-sm text-bad">
          {error === resetAll.error
            ? t('cardBack.approvals.resetFailed')
            : error instanceof ApiError && error.code === 'conflict'
              ? t('cardBack.approvals.conflict')
              : t('cardBack.approvals.changeFailed')}
        </p>
      )}

      {approvals.entries.length === 0 ? null : (
        <ul
          aria-label={t('cardBack.approvals.listLabel')}
          className="flex flex-col gap-2"
        >
          {approvals.entries.map((entry) => (
            <li key={entry.user.id} className="flex min-w-0 items-start gap-2 text-sm">
              <Avatar profile={entry.user} size="sm" />
              <div className="flex min-w-0 flex-col">
                <span className="flex min-w-0 items-center gap-1.5 text-ink">
                  <span className="min-w-0 truncate">{profileName(entry.user)}</span>
                  <span
                    className={cn(
                      'shrink-0 text-xs font-medium',
                      entry.verdict === 'approved' ? 'text-ok' : 'text-bad',
                    )}
                  >
                    {entry.verdict === 'approved'
                      ? t('cardBack.approvals.verdictApproved')
                      : t('cardBack.approvals.verdictRejected')}
                  </span>
                </span>
                {/*
                  When the verdict was given: `updatedAt`, not `createdAt`, so
                  a member who switches from rejected to approved shows the
                  time of the approval. Date AND time, in the member's own
                  language and timezone preferences.
                */}
                <time dateTime={entry.updatedAt} className="text-xs text-ink-muted">
                  {formatLocaleDateTimeLabel(t, entry.updatedAt, localePreferences)}
                </time>
                {entry.reason === null ? null : (
                  <p className="whitespace-pre-wrap break-words text-ink-muted">
                    {entry.reason}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
